import * as React from "react";
import { MoreHorizontalIcon, PlusIcon } from "lucide-react";

import { cn } from "../../../lib/utils";
import { useIsMobile } from "../../../lib/use-mobile";
import { ActionMenuList, type ActionMenuListItem } from "../../ui/action-menu-list";
import { Button } from "../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "../../ui/drawer";
import { useDataTableMobileLabels } from "./data-table-mobile-list";

type ButtonVariant = React.ComponentProps<typeof Button>["variant"];
type ButtonSize = React.ComponentProps<typeof Button>["size"];

export type DataTableToolbarRenderContext = {
  isMobile: boolean;
  inOverflow: boolean;
  close: () => void;
};

/**
 * Declarative toolbar action. The toolbar fully owns how this renders on each breakpoint:
 * - `emphasis: "primary"` → stays a visible filled button (icon-only on mobile, icon+label on desktop).
 * - `emphasis: "priority"` → stays a visible outline/ghost button before overflow.
 * - `emphasis: "secondary"` (default) → folded into the `⋯` overflow in the configured toolbar.
 * Use `render` only for self-contained widgets that own their own dialog/menu (e.g. ExportAction).
 */
export type DataTableAction = {
  id: string;
  label: string;
  /** Shown under the label in the phone's ⋯ sheet. */
  description?: string;
  icon?: React.ReactNode;
  onSelect?: () => void;
  href?: string;
  variant?: ButtonVariant;
  emphasis?: "primary" | "priority" | "secondary";
  disabled?: boolean;
  loading?: boolean;
  hideOnMobile?: boolean;
  order?: number;
  testId?: string;
  /** Sub-actions. In the `⋯` overflow this renders as a collapsible row that reveals these inline. */
  children?: Array<Pick<DataTableAction, "id" | "label" | "icon" | "onSelect" | "disabled" | "variant">>;
  render?: (ctx: DataTableToolbarRenderContext) => React.ReactNode;
  /**
   * A `render` action that draws an icon-only control on phones (export's download button), so it may sit in the
   * phone title row. Other `render` actions (a filter select, a help dialog) never do and fold into ⋯.
   */
  mobileIconOnly?: boolean;
};

/** @deprecated Prefer {@link DataTableAction}. Opaque pre-rendered node; folds into the `⋯` overflow on mobile. */
export type DataTableToolbarAction = {
  id: string;
  node: React.ReactNode;
  mobileSpan?: "half" | "full";
  mobileOrder?: number;
  hideOnMobile?: boolean;
};

export type DataTableToolbarActionInput = DataTableAction | DataTableToolbarAction;

function isLegacyAction(action: DataTableToolbarActionInput): action is DataTableToolbarAction {
  return "node" in action;
}

type NormalizedAction = {
  id: string;
  emphasis: "primary" | "priority" | "secondary";
  hideOnMobile: boolean;
  order: number;
  legacy: boolean;
  source: DataTableToolbarActionInput;
};

function normalizeActions(actions: DataTableToolbarActionInput[] | undefined): NormalizedAction[] {
  if (!actions || actions.length === 0) return [];

  return actions
    .map<NormalizedAction>((action, index) => {
      if (isLegacyAction(action)) {
        return {
          id: action.id,
          emphasis: "secondary",
          hideOnMobile: Boolean(action.hideOnMobile),
          order: action.mobileOrder ?? index,
          legacy: true,
          source: action,
        };
      }

      return {
        id: action.id,
        emphasis: action.emphasis ?? "secondary",
        hideOnMobile: Boolean(action.hideOnMobile),
        order: action.order ?? index,
        legacy: false,
        source: action,
      };
    })
    .sort((left, right) => left.order - right.order);
}

/**
 * The toolbar action's label, WITHOUT a second Radix slot.
 *
 * This used to wrap `children` in `<TooltipTrigger asChild>`. That child is a `Button`, which itself renders
 * `Slot.Root` whenever `asChild` is set (it is, for any action with an `href`) — so two Radix slots landed on
 * one element. Slot rebuilds `composeRefs(...)` unmemoised on every render and a trigger stores its node in
 * state, so attaching the ref dispatched state, which re-rendered, which re-attached: "Maximum update depth
 * exceeded", thrown from inside `composeRefs` with no component of ours in the stack.
 *
 * `title` gives the same hover label with no trigger, no slot and no ref composition — the same resolution the
 * overflow menu, the filters sheet and the view-options menu in this folder already use.
 */
function toolbarTooltipTitle(label: React.ReactNode): string | undefined {
  return typeof label === "string" && label.length > 0 ? label : undefined;
}

function iconButtonSize(size: ButtonSize): ButtonSize {
  if (size === "xs") return "icon-xs";
  if (size === "sm") return "icon-sm";
  if (size === "lg") return "icon-lg";
  return "icon";
}

/** Inline button for a declarative action — icon-only on mobile, icon+label on desktop. */
function ActionInlineButton({
  action,
  size,
  isMobile,
}: {
  action: DataTableAction;
  size: ButtonSize;
  isMobile: boolean;
}) {
  if (action.render) {
    return <>{action.render({ isMobile, inOverflow: false, close: () => undefined })}</>;
  }

  const visualLabel = action.label;
  // Phone headers draw every action as a round icon button, so a primary action declared without an icon gets
  // a "+" there; otherwise its label was clipped inside the circle ("Invit…").
  const icon = action.icon ?? (isMobile && action.emphasis === "primary" ? <PlusIcon /> : undefined);
  const iconOnly = Boolean(icon) && (isMobile || action.emphasis === "priority");
  const label = <span className={iconOnly ? "sr-only" : undefined}>{visualLabel}</span>;
  // While loading, the Button renders its own spinner. Drop the action's own icon so the spinner REPLACES it
  // rather than sitting next to it — otherwise an icon-only button (e.g. Refresh) shows spinner + icon.
  const inner = (
    <>
      {action.loading ? null : icon}
      {label}
    </>
  );

  const button = (
    <Button
      type='button'
      // A "primary"-emphasis action (e.g. the page's create/"Add" button) defaults to a filled primary
      // button so it reads as the important action; "secondary" actions stay as a subtle outline button.
      variant={action.variant ?? (action.emphasis === "primary" ? "default" : action.emphasis === "priority" ? "ghost" : "outline")}
      size={iconOnly ? iconButtonSize(size) : size}
      onClick={action.onSelect}
      disabled={action.disabled}
      loading={action.loading}
      aria-label={action.label}
      title={toolbarTooltipTitle(action.label)}
      data-testid={action.testId}
      className='gap-2'
      asChild={Boolean(action.href)}
    >
      {action.href ? <a href={action.href}>{inner}</a> : inner}
    </Button>
  );

  return button;
}

/**
 * Toolbar `⋯` overflow. A bottom sheet on mobile, a popover on desktop. Built directly (not via
 * DropdownMenuContent) so it can host both clean declarative rows AND self-contained `render` widgets
 * (e.g. ExportAction) without the menu-context constraints of the flattened dropdown sheet.
 */
function ToolbarOverflowMenu({
  items,
  end,
  size,
}: {
  items: NormalizedAction[];
  end?: React.ReactNode;
  size: ButtonSize;
}) {
  const isMobile = useIsMobile();
  const mobileLabels = useDataTableMobileLabels();
  const [open, setOpen] = React.useState(false);
  const close = React.useCallback(() => setOpen(false), []);

  const wrapPad = isMobile ? "px-3 py-2" : "px-1.5 py-1";

  /**
   * Embedded widgets keep their own layout; only their SURFACE is neutralised, so a filled primary button does
   * not sit as a coloured block among ghost rows. Overriding padding/height here broke the rows outright.
   */
  const wrapWidget = cn(
    wrapPad,
    "[&>*]:w-full",
    "[&_button]:bg-transparent [&_button]:shadow-none [&_button]:!text-popover-foreground [&_button:hover]:!bg-muted/70 dark:[&_button:hover]:!bg-white/[0.06]",
  );

  const rows = (
    <div className={cn("flex w-full flex-col", isMobile ? "divide-y divide-border/60" : "gap-0.5")}>
      <ActionMenuList
        close={close}
        items={items.map<ActionMenuListItem>((item) => {
          if (item.legacy) {
            return {
              id: item.id,
              label: item.id,
              render: () => (
                <div className={cn(wrapWidget, "rounded-md hover:bg-muted/70 dark:hover:bg-white/[0.06]")}>
                  {(item.source as DataTableToolbarAction).node}
                </div>
              ),
            };
          }

          const action = item.source as DataTableAction;

          return {
            id: action.id,
            label: action.label,
            description: action.description,
            icon: action.icon,
            disabled: action.disabled,
            variant: action.variant === "destructive" ? "destructive" : "default",
            onSelect: action.onSelect,
            children: action.children?.map((child) => ({
              id: child.id,
              label: child.label,
              icon: child.icon,
              disabled: child.disabled,
              variant: child.variant === "destructive" ? "destructive" : "default",
              onSelect: child.onSelect,
            })),
            render: action.render
              ? () => action.render?.({ isMobile, inOverflow: true, close })
              : undefined,
          };
        })}
      />
      {end ? <div className={cn(wrapWidget, "rounded-md hover:bg-muted/70 dark:hover:bg-white/[0.06]")}>{end}</div> : null}
    </div>
  );

  const trigger = (
    <Button
      type='button'
      variant='outline'
      size={iconButtonSize(size)}
      aria-label={mobileLabels.moreActions}
      title={mobileLabels.moreActions}
      className={cn(isMobile && "size-11 rounded-full")}
    >
      <MoreHorizontalIcon />
    </Button>
  );

  // A phone gets a bottom sheet with full-width rows, like every other sheet in the mobile app.
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className='sr-only'>
            <DrawerTitle>{mobileLabels.moreActions}</DrawerTitle>
            <DrawerDescription>{mobileLabels.moreActions}</DrawerDescription>
          </DrawerHeader>
          <div className='mx-3 mt-2 mb-[max(1rem,env(safe-area-inset-bottom))] overflow-hidden rounded-2xl bg-muted/50'>{rows}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align='end'
          sideOffset={isMobile ? 8 : 4}
          className={cn("w-56 p-1", isMobile && "z-50 w-60 overflow-hidden rounded-[14px] p-0 shadow-[var(--shadow-popover)]")}
        >
          {rows}
        </PopoverContent>
      </Popover>
    </>
  );
}

/**
 * A `⋯` menu that opens to reveal exactly one row is pure indirection: two clicks for one action, and
 * the trigger icon says nothing about what it hides. When a single action would be folded, render it
 * inline instead.
 *
 * Guards:
 * - desktop only — on mobile the overflow is a bottom sheet that keeps the action's label, whereas an
 *   inline button collapses to icon-only; the sheet is the better affordance there.
 * - never promote an action with `children`: the inline button path wires `onSelect` and ignores
 *   sub-actions, so a promoted parent would render a button that does nothing.
 * - never promote a legacy `node` action, and never when `end` content shares the menu — the promoted
 *   button has nowhere to put them.
 *
 * `render` actions (e.g. ExportAction) are safe: they already branch on `inOverflow` and return a
 * self-contained button when it is false.
 */
function promoteLoneOverflowAction(
  overflow: NormalizedAction[],
  { isMobile, hasEnd }: { isMobile: boolean; hasEnd: boolean },
): { promoted: NormalizedAction | null; rest: NormalizedAction[] } {
  if (isMobile || hasEnd || overflow.length !== 1) return { promoted: null, rest: overflow };

  const only = overflow[0];
  if (!only || only.legacy) return { promoted: null, rest: overflow };

  const source = only.source as DataTableAction;
  if (source.children?.length) return { promoted: null, rest: overflow };

  // Render it as `priority` so it joins the ghost icon cluster (filter / display / refresh) rather than
  // shouting as a second outline CTA next to the primary action. `render` actions ignore emphasis.
  return {
    promoted: { ...only, emphasis: "priority", source: { ...source, emphasis: "priority" } },
    rest: [],
  };
}

export function DataTableToolbarActions({
  primaryAction,
  secondaryActions = [],
  size = "default",
}: {
  /** One or more first-class actions. All stay inline; none fold into the `⋯` overflow. */
  primaryAction?: DataTableAction | DataTableAction[];
  secondaryActions?: DataTableAction[];
  size?: ButtonSize;
}) {
  const isMobile = useIsMobile();
  const primaryList = primaryAction ? (Array.isArray(primaryAction) ? primaryAction : [primaryAction]) : [];
  const primary = normalizeActions(primaryList);
  const secondary = normalizeActions(secondaryActions);
  const priority = secondary.filter((action) => !action.legacy && action.emphasis === "priority");
  const foldable = secondary.filter((action) => action.legacy || action.emphasis !== "priority");
  const { promoted, rest: overflow } = promoteLoneOverflowAction(foldable, { isMobile, hasEnd: false });

  return (
    <div className={isMobile ? "contents" : "flex shrink-0 items-center gap-1"}>
      {priority.map((action) => (
        <ActionInlineButton
          key={action.id}
          action={action.source as DataTableAction}
          size={size}
          isMobile={isMobile}
        />
      ))}
      {promoted ? (
        <ActionInlineButton
          key={promoted.id}
          action={promoted.source as DataTableAction}
          size={size}
          isMobile={isMobile}
        />
      ) : null}
      {overflow.length > 0 ? <ToolbarOverflowMenu items={overflow} size={size} /> : null}
      {primary.map((action) => (
        <ActionInlineButton
          key={action.id}
          action={action.source as DataTableAction}
          size={size}
          isMobile={isMobile}
        />
      ))}
    </div>
  );
}

type DataTableToolbarProps = {
  search?: React.ReactNode;
  /** Filter trigger (e.g. the Filters button/sheet). Sits in the compact row, left of the actions. */
  filter?: React.ReactNode;
  start?: React.ReactNode;
  columns?: React.ReactNode;
  filters?: React.ReactNode;
  end?: React.ReactNode;
  toolbarActions?: DataTableToolbarActionInput[];
  actionButtonSize?: ButtonSize;
  mobileSearchFullWidth?: boolean;
  className?: string;
};

export function DataTableToolbar({
  search,
  filter,
  start,
  columns,
  filters,
  end,
  toolbarActions,
  actionButtonSize = "default",
  mobileSearchFullWidth = true,
  className,
}: DataTableToolbarProps) {
  const isMobile = useIsMobile();
  const normalized = React.useMemo(() => normalizeActions(toolbarActions), [toolbarActions]);

  const hasAnything =
    Boolean(search) ||
    Boolean(filter) ||
    Boolean(start) ||
    Boolean(columns) ||
    Boolean(filters) ||
    Boolean(end) ||
    normalized.length > 0;
  if (!hasAnything) return null;

  if (isMobile) {
    const visible = normalized.filter((action) => !action.hideOnMobile);
    const primary = visible.filter((action) => !action.legacy && (action.emphasis === "primary" || action.emphasis === "priority"));
    const overflowItems = visible.filter((action) => action.legacy || (action.emphasis !== "primary" && action.emphasis !== "priority"));
    const hasOverflow = overflowItems.length > 0 || Boolean(end);

    return (
      <div className={cn("flex w-full min-w-0 items-center gap-2", className)}>
        {start ? <div className='shrink-0'>{start}</div> : null}
        {search ? (
          <div className={cn("min-w-0", mobileSearchFullWidth ? "flex-1" : "flex-1")}>{search}</div>
        ) : null}
        {filter ? <div className='shrink-0'>{filter}</div> : null}
        {primary.map((action) => (
          <div key={action.id} className='shrink-0'>
            <ActionInlineButton action={action.source as DataTableAction} size={actionButtonSize} isMobile />
          </div>
        ))}
        {hasOverflow ? (
          <div className='shrink-0'>
            <ToolbarOverflowMenu items={overflowItems} end={end} size={actionButtonSize} />
          </div>
        ) : null}
      </div>
    );
  }

  const renderInline = (action: (typeof normalized)[number]) =>
    action.legacy ? (
      <div key={action.id} className='min-w-0'>
        {(action.source as DataTableToolbarAction).node}
      </div>
    ) : (
      <div key={action.id} className='min-w-0'>
        <ActionInlineButton action={action.source as DataTableAction} size={actionButtonSize} isMobile={false} />
      </div>
    );

  // Declutter crowded toolbars on desktop too (mobile already does this): once there are several low-priority
  // actions, keep the primary action(s) inline and fold the rest (+ `end`, e.g. Refresh) into the `⋯` overflow.
  // Sparse toolbars (the common case) stay fully inline, so most tables are unaffected.
  const desktopPrimary = normalized.filter((action) => !action.legacy && (action.emphasis === "primary" || action.emphasis === "priority"));
  const desktopExtras = normalized.filter((action) => action.legacy || (action.emphasis !== "primary" && action.emphasis !== "priority"));
  const foldExtras = desktopExtras.length + (end ? 1 : 0) >= 3;

  return (
    <div className={cn("flex w-full min-w-0 flex-row items-center gap-3", className)}>
      {start ? <div className='shrink-0'>{start}</div> : null}
      {search ? (
        <div className='min-w-0 sm:flex-1 sm:basis-80 sm:max-w-96'>{search}</div>
      ) : null}
      {columns ? <div className='shrink-0'>{columns}</div> : null}
      {filter ? <div className='shrink-0'>{filter}</div> : null}
      {filters}
      {end || normalized.length > 0 ? (
        <div className='ml-auto flex shrink-0 items-center gap-3'>
          {foldExtras ? (
            <>
              {desktopPrimary.map(renderInline)}
              <ToolbarOverflowMenu items={desktopExtras} end={end} size={actionButtonSize} />
            </>
          ) : (
            <>
              {end}
              {normalized.map(renderInline)}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
